import { cn } from '@/lib/cn';
import { site } from '@/data/site';
import { sections } from '@/data/sections';
import { GlyphField } from '@/scenes/GlyphField';
import { Marquee } from '@/components/chrome/Marquee';

/** Must match `--font-display` in index.css — the field rasterises words in it. */
const DISPLAY_FONT = "'Fraunces Variable', Georgia, serif";

/** Whatever follows the intro in the authored architecture — never hardcoded. */
const SECTION_AFTER_INTRO = sections[1]!;

interface IntroProps {
  /** True once the loader curtain is clear — gates the entrance sequence. */
  readonly ready: boolean;
}

/**
 * The hero. Everything visible here comes from `site.ts` (CLAUDE.md §9: zero
 * content hardcoded in JSX), including the statement itself, which is
 * `roleLine` restated at poster scale rather than a new claim.
 *
 * The headline is DOM text and is the LCP element (§10). The glyph field
 * mounts behind it in V7-2 and must never take that role.
 *
 * Per-word transition delays are inline because they are derived from the
 * loop index — Tailwind cannot generate a utility it can't see at build time.
 * Everything else uses tokens.
 */
export function Intro({ ready }: IntroProps) {
  return (
    <section
      id="intro"
      className="relative flex min-h-svh flex-col justify-between px-(--gutter) pt-28 pb-(--gutter)"
    >
      <GlyphField words={site.heroFieldWords} displayFont={DISPLAY_FONT} />

      <div className="relative flex flex-1 flex-col justify-center">
        <h1>
          <span className="line-mask">
            <span
              className={cn(
                'label ease-settle block transition-transform duration-700',
                ready ? 'translate-y-0' : 'translate-y-full',
              )}
            >
              {site.name} · {site.location}
            </span>
          </span>

          <span className="display text-hero mt-6 block">
            {site.heroStatement.map((word, i) => (
              <span key={word} className="line-mask">
                <span
                  className={cn(
                    'ease-settle relative inline-block transition-transform duration-1000',
                    ready ? 'translate-y-0' : 'translate-y-full',
                  )}
                  style={{ transitionDelay: `${120 + i * 90}ms` }}
                >
                  {word}
                  {word === site.heroAccentWord && (
                    <span
                      aria-hidden="true"
                      className={cn(
                        'bg-acid ease-settle absolute -bottom-1 left-0 h-0.75 w-full origin-left transition-transform duration-700 md:-bottom-2 md:h-1.25',
                        ready ? 'scale-x-100' : 'scale-x-0',
                      )}
                      style={{ transitionDelay: '900ms' }}
                    />
                  )}
                </span>
              </span>
            ))}
          </span>
        </h1>

        <div className="line-mask mt-10 max-w-md">
          <p
            className={cn(
              'text-muted text-body ease-settle transition-transform duration-700',
              ready ? 'translate-y-0' : 'translate-y-full',
            )}
            style={{ transitionDelay: '1000ms' }}
          >
            {site.heroSubline}
          </p>
        </div>
      </div>

      {/* Marquee echo — the fold's closing strip. Repeats the real identity
          fields from site.ts; the trailing separator lives inside each item
          so the -50% loop math stays exact. */}
      <Marquee duration={28} className="border-edge mb-6 border-y py-3">
        {site.echoItems.map((item) => (
          <span key={item} className="label flex shrink-0 items-center">
            <span className="text-bone">{item}</span>
            <span aria-hidden="true" className="px-8 text-acid">
              ✳
            </span>
          </span>
        ))}
      </Marquee>

      {/* Bottom-left is the fixed HudRail's territory at lg and up —
          a "Scroll" label there collided with it, so the cue only appears
          below lg where the readout is hidden. The right-hand item is a real
          anchor rather than a decorative year: the corner does navigation
          work instead of filling space. */}
      <div
        className={cn(
          'ease-settle flex items-end justify-between transition-transform duration-700',
          ready ? 'translate-y-0' : 'translate-y-6',
        )}
        style={{ transitionDelay: '1200ms' }}
      >
        <p className="label lg:hidden">Scroll</p>
        <a
          href={`#${SECTION_AFTER_INTRO.id}`}
          className="label hover:text-acid ml-auto transition-colors"
        >
          {SECTION_AFTER_INTRO.label} ↓
        </a>
      </div>
    </section>
  );
}
